import { useMutation, useReactiveVar } from '@apollo/client';
import { useNavigate } from '@tanstack/react-router';
import type React from 'react';
import { useState } from 'react';
import assetsByCollectionIdPlaylist from 'src/api/collection/assetsByCollectionIdPlaylist';
import deleteItemCollection from 'src/api/collection/deleteItemCollection';
import {
  AUDIO,
  HOME_PATH,
  IMAGE,
  MEDIA_PATH,
  PDF,
  PROXY_FORMAT_SLUG,
  THUMBNAIL_FORMAT_SLUG,
  VIDEO,
} from 'src/constants';
import { MediaCardPreviewModal } from 'src/HomePage/DrawerAndPushedContents/PushedContents/AkomiMediaCardGrid/AkomiMediaCard/MediaCardPreviewModal';
import PlaylistItemRow from 'src/HomePage/DrawerAndPushedContents/RightSideDrawer/PlaylistItemRow';
import { getFormatId } from 'src/helpers/formats';
import viewStateVar from 'src/store/viewState';

interface FormatEdge {
  node: {
    id: string;
    fmtType?: {
      slug: string;
    };
  };
}

interface ContentObject {
  id: string;
  name: string;
  mediatype?: string;
  inpoint?: string;
  timecode?: string;
  formatSet: {
    edges: FormatEdge[];
  };
}

interface CollectionItemNode {
  contentObject: ContentObject;
}

interface CollectionItemEdge {
  node: CollectionItemNode;
}

interface EditCollectionItemProps {
  ss: CollectionItemEdge;
  hoverCard: (fromIndex: number, toIndex: number) => void;
  moveCard: (id: string, toIndex: number) => void;
  index: number;
  id: string;
  clickForPreview?: boolean;
  akomiType?: string;
  collectionData: CollectionItemEdge[];
  flashId?: string | null;
}

const DRAG_TYPE = 'CARD_AKOMI_DRAG';

const EditCollectionItem: React.FC<EditCollectionItemProps> = ({
  ss,
  hoverCard,
  moveCard,
  index,
  id,
  clickForPreview = false,
  akomiType,
  flashId,
}) => {
  const navigate = useNavigate();
  const viewState = useReactiveVar(viewStateVar);
  const [showPreview, setShowPreview] = useState(false);

  const proxyFormat = getFormatId(
    ss.node.contentObject.formatSet.edges as unknown as Array<{ node: { id: string; fmtType: { slug: string } } }>,
    PROXY_FORMAT_SLUG,
  );
  const thumbnailFormat = getFormatId(
    ss.node.contentObject.formatSet.edges as unknown as Array<{ node: { id: string; fmtType: { slug: string } } }>,
    THUMBNAIL_FORMAT_SLUG,
  );

  const [deleteItemHandler] = useMutation(deleteItemCollection, {
    fetchPolicy: 'network-only',
    refetchQueries: [assetsByCollectionIdPlaylist],
  });

  const handleRemove = (): void => {
    deleteItemHandler({
      variables: {
        assetIds: ss.node.contentObject.id,
        collectionId: viewState.selectedCollectionId,
      },
    });
  };

  const handleTitleClick = (): void => {
    navigate({ to: `${HOME_PATH}/media-page/${ss.node.contentObject.id}` });
    viewStateVar({
      ...viewStateVar(),
      playlistItemMediaPageIndex: index,
      mediaPageViaPlaylist: true,
      mediaPageViaLibrary: false,
    });
  };

  const handleThumbnailClick = clickForPreview
    ? (): void => {
        if (akomiType?.includes(VIDEO) || akomiType?.includes(AUDIO)) {
          viewStateVar({
            ...viewStateVar(),
            collectionPreviewIndex: index,
            previewModalVideo: {
              inpoint: ss.node.contentObject.inpoint,
              timecode: ss.node.contentObject.timecode,
            },
          });
        } else if (akomiType?.includes(IMAGE)) {
          viewStateVar({
            ...viewStateVar(),
            collectionPreviewIndex: index,
            previewModalThumbnailId: thumbnailFormat ?? '',
            horizontalMediaCardDetails: {
              image: `${MEDIA_PATH}/${proxyFormat}`,
              title: '',
            },
          });
        } else if (akomiType?.includes(PDF)) {
          setShowPreview(true);
        }
      }
    : undefined;

  return (
    <PlaylistItemRow
      id={id}
      name={ss.node.contentObject.name}
      index={index}
      mediatype={akomiType}
      dragType={DRAG_TYPE}
      onRemove={handleRemove}
      onTitleClick={handleTitleClick}
      onThumbnailClick={handleThumbnailClick}
      hoverCard={hoverCard}
      moveCard={moveCard}
      overlay={
        showPreview ? (
          <MediaCardPreviewModal itemID={id} pagination={true} onClose={() => setShowPreview(false)} />
        ) : undefined
      }
      flashId={flashId}
    />
  );
};

export default EditCollectionItem;
